import { useEffect, useRef, useState } from 'react';
import { MonitorDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isTelegramMiniApp } from '../ton/referral';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  return isIos && !isTelegramMiniApp();
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

type Mode = 'telegram' | 'install' | 'ios' | null;

/**
 * Header icon button that adds the app to the home screen on every platform:
 * native `addToHomeScreen()` inside the Telegram Mini App, the browser's
 * install prompt (via `beforeinstallprompt`) on Chromium desktop/Android, and
 * a "Share → Add to Home Screen" hint on iOS Safari. Renders nothing when the
 * shortcut is already added, the app is already installed, or the platform
 * offers no way to install.
 */
export function AddToHomeScreen({ variant = 'icon' }: { variant?: 'icon' | 'block' }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>(null);
  const [hintOpen, setHintOpen] = useState(false);
  const installEvent = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (isTelegramMiniApp()) {
      if (typeof tg?.addToHomeScreen !== 'function') return;
      if (typeof tg.checkHomeScreenStatus === 'function') {
        tg.checkHomeScreenStatus((status) => {
          if (status === 'unknown' || status === 'missed') setMode('telegram');
        });
      } else {
        setMode('telegram');
      }
      return;
    }
    if (isStandalone()) return;
    const onPrompt = (e: Event) => {
      e.preventDefault();
      installEvent.current = e as BeforeInstallPromptEvent;
      setMode('install');
    };
    const onInstalled = () => setMode(null);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    if (isIosSafari()) setMode('ios');
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!hintOpen) return;
    const close = () => setHintOpen(false);
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [hintOpen]);

  if (mode == null) return null;

  const onClick = () => {
    if (mode === 'telegram') {
      window.Telegram?.WebApp?.addToHomeScreen?.();
    } else if (mode === 'install' && installEvent.current) {
      const ev = installEvent.current;
      void ev.prompt();
      void ev.userChoice.then(({ outcome }) => {
        if (outcome === 'accepted') setMode(null);
      });
    } else {
      setHintOpen((v) => !v);
    }
  };

  return (
    <div className={`a2hs${variant === 'block' ? ' a2hs--block' : ''}`}>
      <button
        type="button"
        className={variant === 'block' ? 'a2hs__btn a2hs__btn--block' : 'a2hs__btn'}
        onClick={onClick}
        title={t('addToHome.button')}
        aria-label={t('addToHome.button')}
      >
        <MonitorDown size={variant === 'block' ? 16 : 18} aria-hidden="true" />
        {variant === 'block' && <span>{t('addToHome.button')}</span>}
      </button>
      {hintOpen && <div className="a2hs__hint">{t('addToHome.iosHint')}</div>}
    </div>
  );
}
